import * as React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { Formik } from 'formik';
import { Grid, Skeleton } from '@mui/material';

import { useApi } from '../shared/context';
import {
	CerebroHeading,
	CerebroText,
	CerebroPrimaryButton,
	CerebroPageContainer,
	CerebroBreadcrumb,
	CerebroStyledBox,
	GetCollateralTypesForDetails,
	BuildCollateralDetailsFormValues,
	CollateralDetailsItem,
	ConvertFormValuesToPatches,
	HasSelectedCollateralTypes,
	CollateralDetailsMutationVariables,
	CerebroForm,
} from '../shared/components';

import { useSnackbar } from 'notistack';
import { FormikUtils } from '../shared/utils';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { DealFinancialMetric, DealRequestAllocation } from '../shared/models';
import {
	QueryKeys,
	useDealRequestAllocationQuery,
	useNavigateToNextPage,
	useDealFinancialMetricsQuery,
} from '../shared/hooks';
import { getApiResponseData } from '../shared/utils/axios';

export const BorrowerCollateralDetails: React.FC = () => {
	const api = useApi();
	const { guid } = useParams<'guid'>();
	const navigateToNextPage = useNavigateToNextPage();

	const client = useQueryClient();
	const snackbar = useSnackbar();

	const allocationQuery = useDealRequestAllocationQuery(guid);

	const collateralTypes = GetCollateralTypesForDetails(allocationQuery.data, 'Borrower');
	const needsToProvideDetails = HasSelectedCollateralTypes(allocationQuery.data);

	const metricsQuery = useDealFinancialMetricsQuery(guid, 'Borrower', collateralTypes);

	const saveChangesMutation = useMutation(
		async ({ values }: CollateralDetailsMutationVariables) => {
			let updatedMetrics: DealFinancialMetric[] | null = null;
			let metrics: DealFinancialMetric[] | null = null;

			const metricPatches = ConvertFormValuesToPatches(collateralTypes, values, 'Borrower');

			if (metricPatches.length > 0) {
				const { data, error } = await getApiResponseData<DealFinancialMetric[]>(
					api.patch(CerebroApiEndpoints.Metrics.Root(guid), metricPatches)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				updatedMetrics = data ?? [];
			}

			let updatedAllocation: DealRequestAllocation | null = null;

			return { updatedMetrics, updatedAllocation };
		},
		{
			onSuccess: ({ updatedMetrics, updatedAllocation }) => {
				if (updatedMetrics) {
					const [_url, metricTypes] = CerebroApiEndpoints.Metrics.FilteredList(
						guid,
						'Borrower',
						collateralTypes
					);
					client.invalidateQueries(QueryKeys.FinancialMetrics.Filtered(guid, 'Borrower', metricTypes));

					snackbar.enqueueSnackbar('Collateral Details Saved.', { variant: 'success' });
				}

				if (updatedAllocation) {
					client.setQueryData(QueryKeys.Allocation(guid), updatedAllocation);
					client.invalidateQueries(QueryKeys.Matching.Get(guid));
				}

				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Financials &gt; Borrower &gt; Collateral Details</CerebroBreadcrumb>

			<CerebroHeading size="h1" component="h1" gutterBottom>
				Enter the balance sheet values from your most recent balance sheet.
			</CerebroHeading>

			{(allocationQuery.isLoading || metricsQuery.isLoading) && <Skeleton height={400} />}

			{allocationQuery.isSuccess && metricsQuery.isSuccess && needsToProvideDetails && (
				<Formik
					enableReinitialize
					initialValues={BuildCollateralDetailsFormValues(
						metricsQuery.data,
						allocationQuery.data,
						'borrower',
						collateralTypes
					)}
					onSubmit={FormikUtils.MutateOnSubmit(saveChangesMutation.mutate)}
				>
					{({ isSubmitting, handleSubmit }) => (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid container alignItems="center" spacing={3} sx={{ paddingY: 3 }}>
								{collateralTypes.map(type => (
									<CollateralDetailsItem key={type} type={type} disabled={isSubmitting} />
								))}
							</Grid>

							<CerebroPrimaryButton loading={isSubmitting} type="submit" sx={{ marginTop: 3 }}>
								Save &amp; Continue
							</CerebroPrimaryButton>
						</CerebroForm>
					)}
				</Formik>
			)}

			{!needsToProvideDetails && allocationQuery.isSuccess && metricsQuery.isSuccess && (
				<CerebroStyledBox sx={{ marginTop: 3 }}>
					<CerebroText>No Collateral Types Selected</CerebroText>
				</CerebroStyledBox>
			)}
		</CerebroPageContainer>
	);
};
